import * as React from 'react';
import {useState} from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';

import {Breadcrumb} from "features/template/types";
import {useDeviceStore, selectIsMobile} from 'features/ui/store/useDeviceStore';
import {getLocalizedRok} from 'features/services/utils/env-helper';
import {PremisesType} from 'components/pages/PremisesStartPage/types';
import {translatePremiseEnum} from 'components/pages/PremisesStartPage/helpers';
import {gtmEventsStagePageInfoOpen} from 'features/services/googleTagManager/events';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';
import {siteReviewAttributes, SiteReviewSource} from 'features/siteReview/siteReview';
import {Margin, HeadlineLevel, HeadlineFont} from 'features/ui/enums';

import './_mainInfo.scss';

import {P} from 'components/atoms/Text/P';
import {Headline} from '@atoms/Text/Headline';
import {UserAgent} from 'components/atoms/UserAgent';
import {MainGrid} from 'components/atoms/Layout/MainGrid';
import {ExpandButton} from '@atoms/LinksAndButtons/ExpandButton';
import {ResidenceTags} from 'components/atoms/Tags/ResidenceTags';
import { QuickFacts } from '@molecules/ResidencePageComponents/QuickFacts';
import {CollapsableText} from 'components/molecules/TextShowHide/CollapsableText';
import {Breadcrumbs} from 'components/molecules/NavigationComponents/Breadcrumbs';
import {Button, ButtonColor, ButtonSize, ButtonType} from 'components/atoms/LinksAndButtons/Button';

export enum QuickFactsType {
    MY_OBJECT = 'myObject',
    OBJECT = 'object',
    PREMISES = 'premises',
    REFERENCE_PROJECT = 'referenceProject',
    RENTAL_PROJECT = 'rentalProject',
    STAGE = 'stage',
    PARKING = 'parking',
    SUCCESSION = 'succession'
}
export enum ExpandedSections {
    NONE = 'none', //default
    QUICKFFACTS = 'quickfacts',
    ALL = 'all'
}

interface Props {
    breadcrumbs?: Breadcrumb[];
    header?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- CMS view model with many dynamic property accesses
    viewModel?: any;
    children?: React.ReactNode;
    quickFactsType: QuickFactsType;
    expandedSections?: ExpandedSections;

    scrollTo?: () => void,
    scrollRefs?: { [key: string] : React.RefObject<HTMLDivElement> }
}

export const MainInfo: React.FunctionComponent<Props> = ({
    breadcrumbs, header, viewModel, children, quickFactsType, 
    expandedSections = ExpandedSections.NONE,
    scrollRefs
}) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const [showMore, setShowMore] = useState<boolean>(false);

    const toggleShowMore = () => {
        if (!showMore){
            gtmEventsStagePageInfoOpen(window.location.pathname)();
        }
        if (showMore && isMobile && scrollRefs) {
            scrollToReference(scrollRefs.overview);
        }
        setShowMore(!showMore);
    };
    const showMoreClass = classnames('residence-info-section__side-content', {
        'show-more': showMore && !isMobile
    });

    const scrollToReference = (reference: React.RefObject<HTMLDivElement> | undefined, smooth: boolean = true) => {
        if (!reference?.current) return;

        window.scroll({
            top: (reference.current.getBoundingClientRect().top + window.scrollY) - (isMobile ? 78 : 110), //reference.current.offsetTop - 100,
            behavior: smooth ? 'smooth' : 'auto',
        });
    };

    const showAllQuickfacts = expandedSections === ExpandedSections.ALL || expandedSections === ExpandedSections.QUICKFFACTS;

    const isObject = quickFactsType === QuickFactsType.OBJECT || quickFactsType === QuickFactsType.MY_OBJECT;
    const bodyText = isObject
        ? viewModel.sellingDescription
        : viewModel.body;
    // Säljbeskrivningen kommer från CRM; objektets id är samma GUID som CRM-posten
    const bodyTextAttributes = isObject ? siteReviewAttributes(SiteReviewSource.CRM, viewModel.objectId) : {};

    return (
        <MainGrid className="residence-info-section info-section" fullwidth>
            <div
                className="residence-info-section__main-content"
                ref={scrollRefs && scrollRefs.overview}
            >
                <Headline
                    level={quickFactsType === QuickFactsType.MY_OBJECT ? HeadlineLevel.H3 : HeadlineLevel.H1} 
                    margin={quickFactsType === QuickFactsType.MY_OBJECT ?  undefined : Margin.S} 
                    className="notranslate"
                >
                    {header}
                </Headline>
                
                {viewModel.possibleRooms &&
                    <Headline 
                        level={HeadlineLevel.H5} 
                        font={HeadlineFont.SERIF} 
                        margin={Margin.S}
                        className="residence-info-section__possible-rooms" 
                    >
                        <FormattedMessage
                            id='jm.components.ObjectPage.subHeadline'
                            values={{possibleRoomsWithUnit: getLocalizedRok(viewModel.possibleRooms)}}
                        />
                    </Headline>
                }
                {viewModel.premisesTags &&
                    <Headline level={HeadlineLevel.H3}>
                        {viewModel.premisesTags.map((x, index) => (
                            <React.Fragment key={`premises-type-${index}`}>
                                <span>{translatePremiseEnum((x.id as PremisesType))}</span>
                                {index !== viewModel.premisesTags.length - 1 ? ', ': '' }
                            </React.Fragment>
                        ))}
                    </Headline>
                }

                {breadcrumbs &&
                    <Breadcrumbs
                        breadcrumbs={breadcrumbs}
                        noPaddingSides
                        noPaddingTop
                        noScroll
                        old
                        residence
                        reference={quickFactsType === QuickFactsType.REFERENCE_PROJECT}
                    />
                }

                {viewModel.tagline &&
                    <P>
                        {viewModel.tagline}
                    </P>
                }
                {viewModel.intro &&
                    <P preamble>
                        {viewModel.intro}
                    </P>
                }
                {bodyText &&
                    <>
                        {(expandedSections === ExpandedSections.ALL)
                            ? <P dangerouslySetInnerHTML={{__html: sanitizeHtml(bodyText)}} dataAttributes={bodyTextAttributes}/>
                            : <CollapsableText
                                showMore={showMore}
                                description={bodyText}
                                newLines={quickFactsType === QuickFactsType.OBJECT}
                                id="main-info__body-text"
                                dataAttributes={bodyTextAttributes}
                            />
                        }
                    </>
                }

                {(expandedSections !== ExpandedSections.ALL) &&
                    <UserAgent mobile tablet>
                        <div className="residence-info-section__expand-button-wrapper">
                            <Button
                                onClick={toggleShowMore}
                                ariaExpanded={showMore}
                                ariaControls={"main-info__body-text"}
                                buttonColor={ButtonColor.DEFAULT}
                                buttonSize={ButtonSize.DEFAULT}
                                buttonType={ButtonType.GHOST}
                            >
                                {showMore
                                    ? <FormattedMessage id='jm.components.StagePage.buttonReadLess' />
                                    : <FormattedMessage id='jm.components.StagePage.buttonReadMore' />
                                }
                            </Button>
                        </div>
                    </UserAgent>
                }
            </div>
            {quickFactsType === QuickFactsType.SUCCESSION
                ? <div
                    className={showMoreClass}
                    ref={scrollRefs && scrollRefs.facts}
                >
                    {children}
                </div>
                : <div
                    className={showMoreClass}
                    ref={scrollRefs && scrollRefs.facts}
                >
                    <UserAgent computer>
                        <ResidenceTags
                            concept={viewModel.concept}
                            stageStatus={(quickFactsType === QuickFactsType.STAGE || quickFactsType === QuickFactsType.PARKING) && viewModel.stageStatus}
                            objectStatus={viewModel.objectStatus}
                            referenceProjectType={viewModel.referenceProjectType}
                            rentalStatus={viewModel.rentalStatus}
                            premisesStatus={viewModel.premisesStatus}
                        />
                    </UserAgent>
                    <div id="main-info__fact-box">
                        <div className={(showMore || showAllQuickfacts) ? "quick-facts show-more" : "quick-facts" }>
                            <QuickFacts
                                viewModel={viewModel}
                                quickFactsType={quickFactsType}
                                showMore={showAllQuickfacts ? true : showMore }
                            />
                        </div>
                    </div>

                    {!showAllQuickfacts &&
                        <div className="quick-facts__show-more__wrapper">
                            <UserAgent computer>
                                <ExpandButton
                                    onClick={toggleShowMore}
                                    isOpen={showMore}
                                    controls="main-info__body-text main-info__fact-box"
                                />
                            </UserAgent>
                            <UserAgent mobile tablet>
                                <Button
                                    onClick={toggleShowMore}
                                    className="quick-facts__show-more"
                                    ariaExpanded={showMore}
                                    ariaControls={"main-info__fact-box"}
                                    buttonColor={ButtonColor.DEFAULT}
                                    buttonSize={ButtonSize.DEFAULT}
                                    buttonType={ButtonType.GHOST}
                                >
                                    {showMore ?
                                        <FormattedMessage id="jm.components.QuickFacts.lessFacts" /> :
                                        <FormattedMessage id="jm.components.QuickFacts.moreFacts" />
                                    }
                                </Button>
                            </UserAgent>
                        </div>
                    }
                </div>
            }

            {(expandedSections !== ExpandedSections.ALL) &&
                <UserAgent computer>
                    <div className="residence-info-section__expand-button-wrapper">
                        <Button
                            onClick={toggleShowMore}
                            ariaExpanded={showMore}
                            ariaControls={"main-info__body-text main-info__fact-box"}
                            buttonColor={ButtonColor.DEFAULT}
                            buttonSize={ButtonSize.DEFAULT}
                            buttonType={ButtonType.GHOST}
                        >
                            {showMore
                                ? quickFactsType === QuickFactsType.SUCCESSION
                                    ? <FormattedMessage id='jm.components.StagePage.buttonReadLess' />
                                    : <FormattedMessage id='jm.components.StagePage.buttonReadLessDesktop' />
                                : <FormattedMessage id='jm.components.StagePage.buttonReadMoreDesktop' />
                            }
                        </Button>
                    </div>
                </UserAgent>
            }
        </MainGrid>
    )
};
